import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GOLD = '#c9a96e';

function formatValue(val, currency) {
  if (!val) return null;
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num) || num === 0) return null;
  let formatted;
  if (num >= 1_000_000) formatted = `${(num / 1_000_000).toFixed(1)}M`;
  else if (num >= 1_000) formatted = `${(num / 1_000).toFixed(0)}k`;
  else formatted = num.toLocaleString();
  return currency ? `${formatted} ${currency}` : formatted;
}

const TopCounterparties = ({ topParties }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (topParties.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: '8px', mb: 0.75 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  const parties = topParties.data?.parties || [];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        height: '100%',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: GOLD,
              letterSpacing: '0.02em',
            }}
          >
            {t('clmDashboard.topCounterparties', 'Top Counterparties')}
          </Typography>
        </Box>

        {parties.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              pb: 0.75,
              mb: 0.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Box sx={{ width: 16 }} />
            <Box sx={{ width: 28 }} />
            <Typography
              variant="caption"
              sx={{ flex: 1, fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              {t('clmDashboard.counterpartyName', 'Name')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 32, textAlign: 'center' }}
            >
              {t('clmDashboard.contracts', 'Contracts')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 40, textAlign: 'right' }}
            >
              {t('clmDashboard.value', 'Value')}
            </Typography>
          </Box>
        )}

        {parties.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 4 }}
          >
            {t('clmDashboard.noParties', 'No counterparties yet')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {parties.slice(0, 8).map((party, index) => {
              const formattedValue = formatValue(party.total_value, party.currency);
              return (
                <Box
                  key={party.party_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    transition: 'background 0.15s',
                    '&:hover': {
                      background: alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  {/* Rank */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: index < 3 ? GOLD : theme.palette.text.secondary,
                      fontSize: '0.75rem',
                      width: 16,
                      textAlign: 'center',
                    }}
                  >
                    {index + 1}
                  </Typography>

                  {/* Icon */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: alpha(GOLD, 0.08),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Users size={14} color={GOLD} />
                  </Box>

                  {/* Name */}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {party.name}
                  </Typography>

                  {/* Contract count */}
                  <Chip
                    label={party.contract_count}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.text.secondary, 0.08),
                      color: theme.palette.text.secondary,
                    }}
                  />

                  {/* Value */}
                  {formattedValue && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: '0.72rem',
                        whiteSpace: 'nowrap',
                        minWidth: 40,
                        textAlign: 'right',
                      }}
                    >
                      {formattedValue}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TopCounterparties;
